import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  ContentLayout,
  Header,
  Container,
  SpaceBetween,
  Alert,
  Spinner,
  Box,
  ColumnLayout,
  CopyToClipboard,
} from "@cloudscape-design/components";
import { SearchForm, CustomerDataPanel, WorkflowPanel, ScheduledEventsPanel, StatusBox, RecentSearchesRow } from "../Components";
import {
  ApiClient,
  AggregatedCustomerDataService,
  WorkflowService,
  ScheduledEventsService,
  CustomerData,
  CustomerDataHistory,
  WorkflowExecution,
  WorkflowStatus,
} from "../services";
import { ScheduledEventsResponse } from "../services/ScheduledEventsService";
import { Realm, RealmLower, Stage, TABLE_NAMES, LABELS, NAV, NotifyFn } from "../constants";
import { CustomerIdEncoder, HashParams, RecentSearches, ResultFormatter, toErrorMessage } from "../utils";
import { RecentSearchEntry } from "../utils/RecentSearches";

interface SearchPageProps {
  onNotify: NotifyFn;
}

interface SearchErrors {
  customerData?: string;
  workflows?: string;
  scheduledEvents?: string;
}

const api = ApiClient.getInstance();
const customerService = new AggregatedCustomerDataService(api);
const workflowService = new WorkflowService(api);
const scheduledEventsService = new ScheduledEventsService(api);

const getUrlParams = (): { customerId: string; realm: Realm } => {
  const params = HashParams.parse();
  return {
    customerId: params.get("customerId") || "",
    realm: (params.get("realm")?.toUpperCase() as Realm) || "NA",
  };
};

const updateUrlParams = (customerId: string, realm: Realm): void => {
  HashParams.update(NAV.ROUTES.SEARCH, customerId ? { customerId, realm: realm.toLowerCase() } : {});
};

export const SearchPage: React.FC<SearchPageProps> = ({ onNotify }) => {
  const urlParams = useMemo(getUrlParams, []);
  const [customerId, setCustomerId] = useState(urlParams.customerId);
  const [realm, setRealm] = useState<Realm>(urlParams.realm);
  const [stage, setStage] = useState<Stage>("beta");
  const [includeHistory, setIncludeHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CustomerData | null>(null);
  const [history, setHistory] = useState<CustomerDataHistory | null>(null);
  const [workflows, setWorkflows] = useState<WorkflowExecution[]>([]);
  const [scheduledEvents, setScheduledEvents] = useState<ScheduledEventsResponse | null>(null);
  const [errors, setErrors] = useState<SearchErrors>({});
  const [searched, setSearched] = useState(false);
  const [searchedId, setSearchedId] = useState("");
  const [searchedRealm, setSearchedRealm] = useState<Realm>(urlParams.realm);
  const [recentSearches, setRecentSearches] = useState<RecentSearchEntry[]>(RecentSearches.list);
  const initialSearchDone = useRef(false);

  const realmLower = useMemo(() => realm.toLowerCase() as RealmLower, [realm]);

  useEffect(() => {
    api.init().then(() => setStage(api.getStage()));
  }, []);

  const performSearch = useCallback(
    async (rawId: string, searchRealm: Realm): Promise<void> => {
      if (!rawId.trim()) {
        return;
      }

      const normalizedId = CustomerIdEncoder.normalize(rawId);
      const searchRealmLower = searchRealm.toLowerCase() as RealmLower;
      setLoading(true);
      setSearched(true);
      setSearchedId(normalizedId);
      setSearchedRealm(searchRealm);
      setErrors({});
      updateUrlParams(normalizedId, searchRealm);
      setRecentSearches(RecentSearches.add({ customerId: normalizedId, realm: searchRealm }));

      try {
        const [customerResult, workflowResult, scheduledEventsResult] = await Promise.all([
          customerService.fetchAll(searchRealmLower, normalizedId, includeHistory),
          workflowService.getWorkflows(searchRealmLower, normalizedId),
          scheduledEventsService.fetchSchedules(searchRealmLower, normalizedId),
        ]);

        const nextErrors: SearchErrors = {};

        if (customerResult.status === "ok") {
          setData(customerResult.data.data);
          setHistory(customerResult.data.history);
        } else {
          setData(null);
          setHistory(null);
          nextErrors.customerData = customerResult.message;
        }

        if (workflowResult.status === "ok") {
          setWorkflows(workflowResult.data);
        } else {
          setWorkflows([]);
          nextErrors.workflows = workflowResult.message;
        }

        if (scheduledEventsResult.status === "ok") {
          setScheduledEvents(scheduledEventsResult.data);
        } else {
          setScheduledEvents(null);
          nextErrors.scheduledEvents = scheduledEventsResult.message;
        }

        setErrors(nextErrors);
      } catch (err) {
        onNotify("error", `Search failed: ${toErrorMessage(err)}`);
      } finally {
        setLoading(false);
      }
    },
    [includeHistory, onNotify]
  );

  const handleSearch = useCallback((): Promise<void> => performSearch(customerId, realm), [performSearch, customerId, realm]);

  const handleRecentSelect = useCallback(
    (entry: RecentSearchEntry): void => {
      setCustomerId(entry.customerId);
      setRealm(entry.realm);
      performSearch(entry.customerId, entry.realm);
    },
    [performSearch]
  );

  const handleClearRecent = useCallback((): void => {
    setRecentSearches(RecentSearches.clear());
  }, []);

  useEffect(() => {
    if (!initialSearchDone.current && urlParams.customerId) {
      initialSearchDone.current = true;
      handleSearch();
    }
  }, [urlParams.customerId, handleSearch]);

  const handleRealmChange = useCallback(
    (newRealm: Realm): void => {
      setRealm(newRealm);
      if (searchedId) {
        updateUrlParams(searchedId, newRealm);
      }
    },
    [searchedId]
  );

  const handleRedrive = useCallback(
    async (executionArn: string): Promise<void> => {
      try {
        await workflowService.redrive(realmLower, executionArn);
        onNotify("success", "Workflow redriven successfully");
        const refreshed = await workflowService.getWorkflows(realmLower, searchedId);
        if (refreshed.status === "ok") {
          setWorkflows(refreshed.data);
          setErrors((prev) => ({ ...prev, workflows: undefined }));
        } else {
          setErrors((prev) => ({ ...prev, workflows: refreshed.message }));
        }
      } catch (err) {
        onNotify("error", `Redrive failed: ${toErrorMessage(err)}`);
      }
    },
    [realmLower, searchedId, onNotify]
  );

  const hasData = data && Object.values(data).some((v) => v !== null);
  const hasErrors = Boolean(errors.customerData || errors.workflows || errors.scheduledEvents);

  const stats = useMemo(
    () => ({
      tablesFound: data ? TABLE_NAMES.filter((t) => data[t] !== null).length : 0,
      historyVersions: history ? TABLE_NAMES.reduce((sum, t) => sum + (history[t]?.length || 0), 0) : 0,
      workflowsTotal: workflows.length,
      workflowsFailed: workflows.filter((w) => w.status === WorkflowStatus.FAILED).length,
    }),
    [data, history, workflows]
  );

  const copyText = useMemo(
    () =>
      ResultFormatter.formatSearchResults({
        customerId: searchedId,
        realm: searchedRealm,
        data,
        history,
        workflows,
        scheduledEvents: scheduledEvents?.schedules || [],
      }),
    [searchedId, searchedRealm, data, history, workflows, scheduledEvents]
  );

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={LABELS.SEARCH_DESCRIPTION}>
          {LABELS.CUSTOMER_SEARCH}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container>
          <SpaceBetween size="m">
            <SearchForm
              customerId={customerId}
              realm={realm}
              stage={stage}
              includeHistory={includeHistory}
              loading={loading}
              onCustomerIdChange={setCustomerId}
              onRealmChange={handleRealmChange}
              onHistoryChange={setIncludeHistory}
              onSearch={handleSearch}
            />
            <RecentSearchesRow entries={recentSearches} onSelect={handleRecentSelect} onClear={handleClearRecent} />
          </SpaceBetween>
        </Container>

        {loading && (
          <Container>
            <Box textAlign="center" padding="xxl">
              <SpaceBetween size="m" alignItems="center">
                <Spinner size="large" />
                <Box variant="p" color="text-status-inactive">
                  {LABELS.SEARCHING}
                </Box>
              </SpaceBetween>
            </Box>
          </Container>
        )}

        {!loading && hasErrors && (
          <SpaceBetween size="s">
            {errors.customerData && (
              <Alert type="error" header={LABELS.CUSTOMER_DATA_ERROR}>
                {errors.customerData}
              </Alert>
            )}
            {errors.workflows && (
              <Alert type="error" header={LABELS.WORKFLOWS_ERROR}>
                {errors.workflows}
              </Alert>
            )}
            {errors.scheduledEvents && (
              <Alert type="error" header={LABELS.SCHEDULED_EVENTS_ERROR}>
                {errors.scheduledEvents}
              </Alert>
            )}
          </SpaceBetween>
        )}

        {!loading && searched && !hasErrors && !hasData && workflows.length === 0 && (
          <Alert type="info" header={LABELS.NO_RESULTS_FOUND}>
            No data found for customer ID: <strong>{searchedId}</strong> in realm <strong>{searchedRealm}</strong>
          </Alert>
        )}

        {!loading && searched && (hasData || workflows.length > 0) && (
          <Container
            header={
              <Header
                variant="h2"
                description={`${LABELS.RESULTS_FOR} ${searchedId}`}
                actions={
                  <CopyToClipboard
                    copyButtonText={LABELS.COPY_RESULTS}
                    copySuccessText={LABELS.COPIED}
                    copyErrorText={LABELS.ERROR}
                    textToCopy={copyText}
                  />
                }
              >
                {LABELS.SEARCH_RESULTS}
              </Header>
            }
          >
            <ColumnLayout columns={4} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">{LABELS.TABLES_FOUND}</Box>
                <Box fontSize="display-l" fontWeight="bold">
                  {stats.tablesFound}
                  <Box variant="small" display="inline" color="text-status-inactive">
                    {" "}
                    / {TABLE_NAMES.length}
                  </Box>
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">{LABELS.HISTORY_VERSIONS}</Box>
                <Box fontSize="display-l" fontWeight="bold">
                  {stats.historyVersions}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">{LABELS.WORKFLOWS}</Box>
                <Box fontSize="display-l" fontWeight="bold">
                  {stats.workflowsTotal}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">{LABELS.FAILED_WORKFLOWS}</Box>
                <Box fontSize="display-l" fontWeight="bold" color={stats.workflowsFailed > 0 ? "text-status-error" : undefined}>
                  {stats.workflowsFailed}
                </Box>
              </div>
            </ColumnLayout>
          </Container>
        )}

        {!loading && hasData && data && (
          <>
            <StatusBox data={data} />
            <CustomerDataPanel data={data} history={history} />
          </>
        )}

        {!loading && workflows.length > 0 && <WorkflowPanel workflows={workflows} loading={false} onRedrive={handleRedrive} />}

        {!loading && scheduledEvents && scheduledEvents.schedules.length > 0 && (
          <ScheduledEventsPanel schedules={scheduledEvents.schedules} />
        )}
      </SpaceBetween>
    </ContentLayout>
  );
};
